import { useEffect, useRef } from "react";
import { replaceHistory } from "@tm/insurance/utils";
import {
  convertDateToUnix,
  convertUnixToDate,
} from "../../../../utils";
import type { Country, Segment, PricingView, TravelInsuranceTab } from "../../CommonLandingMweb.types";
import {
  mirrorPlanTypeSegmentInUrl,
  replaceHistoryPlanTypeAndSegment,
} from "./urlUtils";

interface UrlSyncParams {
  tripOptions: TravelInsuranceTab["tripOptions"];
  travelInsuranceData?: TravelInsuranceTab;
  initialStartDate: Date;
  initialEndDate: Date;
  initialTravellerCount: number;
  selectedTripOptionId: string | null;
  setSelectedTripOptionId: (id: string | null) => void;
  setSelectedCountries: (c: Country[]) => void;
  setFromDate: (d: Date) => void;
  setToDate: (d: Date) => void;
  setTravellerCount: (n: number) => void;
  setPlanSegmentId: (id: string | null) => void;
  setPricingViewId: (id: string | null) => void;
  onCountryChangeRef: React.MutableRefObject<((countries: Country[]) => void) | undefined>;
  onDateChangeRef: React.MutableRefObject<((from: Date, to: Date) => void) | undefined>;
  onTravellerChangeRef: React.MutableRefObject<((count: number) => void) | undefined>;
  onSegmentChangeRef: React.MutableRefObject<((segments: Segment[]) => void) | undefined>;
  onTripOptionChangeRef: React.MutableRefObject<((id: string | null) => void) | undefined>;
}

/**
 * One-time URL → state hydration. Reads query params and pushes values into
 * the component's state + fires parent callbacks so the host page stays in sync.
 */
export function useTravelInsuranceUrlSync({
  tripOptions,
  travelInsuranceData,
  initialStartDate,
  initialEndDate,
  initialTravellerCount,
  selectedTripOptionId,
  setSelectedTripOptionId,
  setSelectedCountries,
  setFromDate,
  setToDate,
  setTravellerCount,
  setPlanSegmentId,
  setPricingViewId,
  onCountryChangeRef,
  onDateChangeRef,
  onTravellerChangeRef,
  onSegmentChangeRef,
  onTripOptionChangeRef,
}: UrlSyncParams) {
  const urlHydratedRef = useRef(false);

  /* Auto-select sole trip option */
  useEffect(() => {
    if (tripOptions.length !== 1) return;
    const id = tripOptions[0]?.id;
    if (!id || selectedTripOptionId === id) return;
    setSelectedTripOptionId(id);
    onTripOptionChangeRef.current?.(id);
  }, [tripOptions, selectedTripOptionId, setSelectedTripOptionId, onTripOptionChangeRef]);

  /* Fire initial date/traveller callbacks */
  useEffect(() => {
    onDateChangeRef.current?.(initialStartDate, initialEndDate);
    onTravellerChangeRef.current?.(initialTravellerCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Pre-populated countries from API (when not already set via URL) */
  useEffect(() => {
    if (typeof window !== "undefined") {
      const countriesInUrl = new URLSearchParams(window.location.search).get("countries");
      if (countriesInUrl && countriesInUrl.length > 0) return;
    }
    const currentTripOption = tripOptions.find((o) => o.id === (
      tripOptions.length >= 2 ? selectedTripOptionId : (tripOptions[0]?.id ?? null)
    ));
    if (!currentTripOption?.countryList?.value?.length) return;
    const prePopulatedCodes = travelInsuranceData?.prePopulatedData?.countryCode || [];
    if (!prePopulatedCodes.length) return;

    const initialCountries = currentTripOption.countryList.value
      .filter((country) => prePopulatedCodes.includes(country?.code))
      .map((c): Country => ({ code: c.code, name: c.name ?? c.code, subtitle: c.subtitle }));
    if (initialCountries.length) {
      setSelectedCountries(initialCountries);
      onCountryChangeRef.current?.(initialCountries);
    }
  }, [tripOptions, selectedTripOptionId, travelInsuranceData?.prePopulatedData?.countryCode, setSelectedCountries, onCountryChangeRef]);

  /* Main URL hydration (one-time) */
  useEffect(() => {
    if (typeof window === "undefined" || urlHydratedRef.current) return;
    if (!tripOptions?.length) return;

    const sp = new URLSearchParams(window.location.search);

    let optionId: string | null =
      tripOptions.length === 1 ? tripOptions[0]?.id ?? null : selectedTripOptionId;

    const urlTrip = sp.get("tripOption");
    if (urlTrip && tripOptions.some((o) => o?.id === urlTrip)) {
      optionId = urlTrip;
      setSelectedTripOptionId(urlTrip);
      onTripOptionChangeRef.current?.(urlTrip);
    }

    const opt = optionId != null ? tripOptions.find((o) => o.id === optionId) : tripOptions[0];

    if (opt?.countryList?.value?.length) {
      const countriesParam = sp.get("countries");
      if (countriesParam) {
        const codes = countriesParam.split(",").map((c) => c.trim()).filter(Boolean);
        const resolved: Country[] = opt.countryList.value
          .filter((c) => c.code && codes.includes(c.code) && (c.type === "COUNTRY" || c.type === "REGION" || !c.type))
          .map((c) => ({ code: c.code as string, name: (c.name ?? c.code) as string, subtitle: c.subtitle }))
          .filter((c) => c.code && c.name);
        if (resolved.length) {
          setSelectedCountries(resolved);
          onCountryChangeRef.current?.(resolved);
        }
      }
    }

    const sd = sp.get("startDate");
    const ed = sp.get("endDate");
    if (sd) {
      const start = convertUnixToDate(parseInt(sd, 10));
      const end = ed ? convertUnixToDate(parseInt(ed, 10)) : initialEndDate;
      setFromDate(start);
      setToDate(end);
      onDateChangeRef.current?.(start, end);
    }

    const tv = sp.get("travellers");
    if (tv) {
      const n = parseInt(tv, 10);
      if (!Number.isNaN(n) && n > 0) {
        setTravellerCount(n);
        onTravellerChangeRef.current?.(n);
      }
    }

    const planTypeParam = sp.get("planType") || sp.get("segment");
    if (planTypeParam && opt?.planTypes?.value?.some((s) => s.id === planTypeParam)) {
      setPlanSegmentId(planTypeParam);
      const next = (opt.planTypes.value || []).map((item) => ({ ...item, isSelected: item.id === planTypeParam }));
      onSegmentChangeRef.current?.(next);
    }

    const pricingViewParam = sp.get("pricingView");
    if (pricingViewParam && opt?.pricingView?.value?.some((p) => p.id === pricingViewParam)) {
      setPricingViewId(pricingViewParam);
    } else if (opt?.pricingView?.value?.length) {
      const defaultPricingView = opt.pricingView.value.find((p) => p.isSelected) ?? opt.pricingView.value[0];
      if (defaultPricingView?.id) setPricingViewId(defaultPricingView.id);
    }

    if (tripOptions.length === 1 && tripOptions[0]?.id && !sp.get("tripOption")) {
      replaceHistory("tripOption", tripOptions[0].id);
    }

    const multiTripDefaultId =
      tripOptions.length >= 2
        ? travelInsuranceData?.prePopulatedData?.tripOption || tripOptions.find((o) => o?.isSelected)?.id || tripOptions[0]?.id || null
        : null;
    if (tripOptions.length >= 2 && multiTripDefaultId && !sp.get("tripOption")) {
      replaceHistory("tripOption", multiTripDefaultId);
    }

    const cur = new URLSearchParams(window.location.search);
    if (!cur.get("startDate")) replaceHistory("startDate", String(convertDateToUnix(initialStartDate)));
    if (!cur.get("endDate")) replaceHistory("endDate", String(convertDateToUnix(initialEndDate)));
    if (!cur.get("travellers")) replaceHistory("travellers", String(initialTravellerCount));

    if (!cur.get("countries") && opt?.countryList?.value?.length) {
      const prePopulatedCodes = travelInsuranceData?.prePopulatedData?.countryCode || [];
      const validCodes = prePopulatedCodes.filter((code) => opt.countryList!.value!.some((c) => c?.code === code));
      if (validCodes.length) replaceHistory("countries", validCodes.join(","));
    }

    const planTypesForDefault = opt?.planTypes?.value;
    if (!cur.get("planType") && !cur.get("segment") && planTypesForDefault?.length) {
      const defaultSeg = planTypesForDefault.find((s) => s.isSelected) ?? planTypesForDefault[0];
      if (defaultSeg?.id) replaceHistoryPlanTypeAndSegment(defaultSeg.id);
    }

    const pricingViewsForDefault = opt?.pricingView?.value;
    if (pricingViewsForDefault?.length) {
      const urlPricingView = cur.get("pricingView");
      const defaultPricingView =
        (urlPricingView && pricingViewsForDefault.find((p) => p.id === urlPricingView)) ||
        pricingViewsForDefault.find((p) => p.isSelected) ||
        pricingViewsForDefault[0];
      if (defaultPricingView?.id) replaceHistory("pricingView", defaultPricingView.id);
    }

    mirrorPlanTypeSegmentInUrl();
    urlHydratedRef.current = true;
  }, [
    tripOptions,
    travelInsuranceData,
    initialStartDate,
    initialEndDate,
    initialTravellerCount,
    selectedTripOptionId,
    setSelectedTripOptionId,
    setSelectedCountries,
    setFromDate,
    setToDate,
    setTravellerCount,
    setPlanSegmentId,
    setPricingViewId,
    onCountryChangeRef,
    onDateChangeRef,
    onTravellerChangeRef,
    onSegmentChangeRef,
    onTripOptionChangeRef,
  ]);
}
